import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaEnvelope, FaLock, FaEye, FaEyeSlash, FaGoogle,
  FaFacebook, FaGem, FaArrowRight
} from 'react-icons/fa';
import { useGoogleLogin } from '@react-oauth/google';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'react-toastify';
import api from '../../api/Api';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useUser();
  const { isDarkMode } = useTheme();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Normal Login ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const body = new FormData();
      body.append('email', formData.email);
      body.append('password', formData.password);

      const { data } = await api.post('login', body);

      if (data.token) {
        localStorage.setItem('token', data.token);
      }

      // Pass user data to context and wait for it to complete
      await login(data.USER || data);

      toast.success(data?.message || 'Login successful!');
      navigate('/profile');

    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.errors?.[Object.keys(error.response?.data?.errors ?? {})[0]]?.[0] ||
        'Login failed. Please check your credentials.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Google Login ──
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);

        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        const profile = await res.json();

        const body = new FormData();
        body.append('id', profile.sub);
        body.append('name', profile.name);
        body.append('email', profile.email);
        body.append('imageUrl', profile.picture ?? '');

        const { data } = await api.post('loginWithGoogle', body);

        if (data.token) {
          localStorage.setItem('token', data.token);
        }

        await login(data.user ?? data);
        navigate('/profile');

      } catch (error) {
        const msg =
          error.response?.data?.message ||
          'Google login failed. Please try again.';
        toast.error(msg);
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => toast.error('Google login cancelled or failed.')
  });

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: isDarkMode
        ? 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 100%)'
        : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)'
    }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row g-0 shadow-lg rounded-4 overflow-hidden">

              {/* Left Side - Image */}
              <div className="col-lg-6 d-none d-lg-block">
                <div className="h-100 position-relative" style={{
                  background: `url('https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?w=800') center/cover`
                }}>
                  <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%)'
                  }}>
                    <div className="p-5 text-white h-100 d-flex flex-column justify-content-center">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}>
                        <FaGem className="mb-4" style={{ fontSize: '50px' }} />
                        <h2 className="mb-3">Welcome Back!</h2>
                        <p className="lead">
                          Login to explore our exclusive collection of premium jewellery
                        </p>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="col-lg-6">
                <div className="p-5" style={{
                  background: isDarkMode ? 'var(--bg-secondary)' : 'white'
                }}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}>

                    {/* Logo */}
                    <div className="text-center mb-4">
                      <Link to="/" className="text-decoration-none">
                        <FaGem className="mb-2" style={{
                          fontSize: '40px',
                          color: 'var(--primary-gold)'
                        }} />
                        <h3 className="gold-text" style={{
                          fontFamily: 'var(--font-secondary)'
                        }}>
                          Bharat Gold
                        </h3>
                      </Link>
                    </div>

                    <h4 className="mb-4 text-center">Sign In to Your Account</h4>

                    {/* Social Login */}
                    <div className="d-flex gap-3 mb-4">
                      <button
                        onClick={() => handleGoogleLogin()}
                        disabled={isLoading}
                        className="btn btn-outline-secondary flex-grow-1 d-flex align-items-center justify-content-center py-2"
                        style={{ borderRadius: '25px' }}>
                        <FaGoogle className="me-2" /> Google
                      </button>
                      <button
                        onClick={() => toast.info('Facebook login coming soon!')}
                        disabled={isLoading}
                        className="btn btn-outline-secondary flex-grow-1 d-flex align-items-center justify-content-center py-2"
                        style={{ borderRadius: '25px' }}>
                        <FaFacebook className="me-2" /> Facebook
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <span className="text-muted">or sign in with email</span>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit}>
                      {/* Email */}
                      <div className="mb-3">
                        <label className="form-label">Email Address</label>
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            borderRadius: '25px 0 0 25px',
                            background: 'transparent',
                            border: '1px solid var(--border-color)'
                          }}>
                            <FaEnvelope style={{ color: 'var(--primary-gold)' }} />
                          </span>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            required
                            style={{
                              borderRadius: '0 25px 25px 0',
                              border: '1px solid var(--border-color)',
                              borderLeft: 'none',
                              background: isDarkMode ? 'var(--bg-primary)' : 'white',
                              color: 'var(--text-primary)'
                            }}
                          />
                        </div>
                      </div>

                      {/* Password */}
                      <div className="mb-3">
                        <label className="form-label">Password</label>
                        <div className="input-group">
                          <span className="input-group-text" style={{
                            borderRadius: '25px 0 0 25px',
                            background: 'transparent',
                            border: '1px solid var(--border-color)'
                          }}>
                            <FaLock style={{ color: 'var(--primary-gold)' }} />
                          </span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter your password"
                            required
                            style={{
                              border: '1px solid var(--border-color)',
                              borderLeft: 'none',
                              borderRight: 'none',
                              background: isDarkMode ? 'var(--bg-primary)' : 'white',
                              color: 'var(--text-primary)'
                            }}
                          />
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              borderRadius: '0 25px 25px 0',
                              border: '1px solid var(--border-color)',
                              borderLeft: 'none',
                              background: 'transparent'
                            }}>
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>

                      {/* Remember Me & Forgot Password */}
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="rememberMe">
                            Remember me
                          </label>
                        </div>
                        <Link to="/forgot-password" className="text-decoration-none" style={{
                          color: 'var(--primary-gold)'
                        }}>
                          Forgot Password?
                        </Link>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="btn btn-luxury w-100 mb-3 d-flex align-items-center justify-content-center"
                        disabled={isLoading}
                        style={{ borderRadius: '25px', padding: '12px' }}>
                        {isLoading ? (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <>
                            Sign In
                            <FaArrowRight className="ms-2" />
                          </>
                        )}
                      </button>
                    </form>

                    {/* Sign Up Link */}
                    <p className="text-center mb-0">
                      Don't have an account?{' '}
                      <Link to="/register" style={{
                        color: 'var(--primary-gold)',
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}>
                        Sign Up
                      </Link>
                    </p>

                  </motion.div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;